import React, { useState, useRef, forwardRef, createRef, RefObject, ReactChildren } from 'react'
import { styled, Button } from '@fareye/ui'
import { camelCase, FaChevronDown, FaMinus, isEqual } from '@fareye/utils'
import getFilterComponent from './getFilterComponent'
import { ISelecetedFilters, ISummaryData } from '../../utility/types'
import { inTransitConstant } from '../../../../config/constants'
import { IPageRenderConfig } from '@fareye/types'

interface IHeaderFilter {
  summaryData: ISummaryData,
  selectedFilters: ISelecetedFilters,
  upsertSelectedFilters: (filterType: string, filterKey: string, filterValue: any) => void,
  additionalDays?: number,
  config: IPageRenderConfig
}

interface IFilter extends IHeaderFilter {
  component: string,
  filterType: string,
  filterKey: string,
  filterTitle: string,
  active: boolean,
  toggleActiveFilter: (key: string) => void
}

const HeaderFiltertContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
`

const FilterWrapper = styled.div`
  position: relative;
  display: flex;
`

const FilterButton = styled(Button)`
    background: #FFFFFF;
    padding: 6px 16px;
    border: 1px solid #F0F0F0;
    border-radius: 10px;
    gap: 4px;
    font-family: lato;
    font-size: 14px;
    line-height: 0;
`

const FilterComponent = styled.div`
  position: absolute;
  width: max-content;
  box-shadow: 0px 0px 12px 0px #8EA1B633;
  z-index: 99;
  background: #fff;
  height: -webkit-fill-available;
  top: 120%;
  left: 0;
  border: 1px solid #F4F5F7
`

const Filter: React.FC<IFilter> = (props) => {
  const { component, filterType, filterKey, filterTitle, active, selectedFilters, toggleActiveFilter, upsertSelectedFilters} = props
  const [initialFilterValue, setInitialFilterValue] = useState<any>(selectedFilters[filterType]?.[filterKey]);
  const [filterValue, setFilterValue] = useState<any>(selectedFilters[filterType]?.[filterKey])
  const title = filterTitle ?? inTransitConstant[camelCase(filterKey)]
  const label = component === 'dateRange' ? "Date Range" : title
  const handleToggle = () => {
    if(active && !isEqual(filterValue,initialFilterValue)) {
      upsertSelectedFilters(filterType, filterKey, filterValue)
      setInitialFilterValue(filterValue)
    }
    toggleActiveFilter(filterKey)
  }
  const wrapperRef = React.useRef(null)
  function useOutsideAlerter(ref: any) {
    function handleClickOutside(event: any) {
      event.stopPropagation()
      if (active && ref.current && !ref.current.contains(event.target)) {
        handleToggle()
      }
    }
    React.useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    })
  }
  useOutsideAlerter(wrapperRef)
  return (
    <FilterWrapper ref={wrapperRef}>
      <FilterButton
        onClick = {() => handleToggle()}
        rightChild = { active ? <FaMinus/> : <FaChevronDown/>}
        rightStyle = {{'height': '20px', 'width': '20px', 'paddingTop': '4px'}}
      >
        {label}
      </FilterButton>
      { active && 
        <FilterComponent>
          {getFilterComponent(component, {...props, filterValue, setFilterValue})}
        </FilterComponent>
      }
    </FilterWrapper>
  )
}

const HeaderFilter: React.FC<IHeaderFilter> = ( props ) => {
  const { config } = props
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const toggleActiveFilter = (key: string) => {
    setActiveFilter(key === activeFilter ? null : key)
  }
  return (
    <HeaderFiltertContainer>
      {
        Object.entries(config).map(([key, configData]) => {
          const component = configData.component
          const {type: filterType, filterTitle: filterTitle} = configData.config
          return (
            <Filter
              key = {`Filter-${key}`}
              component = {component}
              filterType = {filterType}
              filterTitle = {filterTitle}
              filterKey = {key}
              active = {activeFilter === key}
              toggleActiveFilter = {toggleActiveFilter}
              {...props}
            />
          )
        })
      }
    </HeaderFiltertContainer>
  )
}

export default HeaderFilter